'use client';

import { useMemo, type HTMLAttributes } from 'react';

import Image from 'next/image';

import bolt from '@/assets/images/bolt.png';
import halfBolt from '@/assets/images/half-bolt.png';
import { cn } from '@/libs';
import { useModalStore } from '@/stores';
import { formatCurrency, formatRelativeTime, getStateAbbreviationByDDD } from '@/utils';
import {
  EThumbnailQuality,
  extractYoutubeVideoId,
  getYoutubeEmbedUrl,
  getYoutubeThumbnailUrl,
} from '@/utils/video-url';

export type LastWinnerCardVariant = 'default' | 'mainPrize';

const TEXT_MAX_WIDTH = 'max-w-32';

type LastWinnerCardProps = HTMLAttributes<HTMLDivElement> & {
  name: string;
  date: string;
  value: number;
  state: string;
  winnerVideoUrl?: string;
  variant?: LastWinnerCardVariant;
  highlighted?: boolean;
};

const variantStyles: Record<LastWinnerCardVariant, { container: string; value: string }> =
  {
    default: {
      container: '',
      value: 'text-foreground',
    },
    mainPrize: {
      container: 'border border-secondary/40',
      value: 'text-currencyColor',
    },
  };

const getAriaLabel = ({
  name,
  variant,
  highlighted,
}: {
  name: string;
  variant: LastWinnerCardVariant;
  highlighted: boolean;
}) => {
  if (variant !== 'mainPrize') return undefined;

  return `${name}, prêmio principal${highlighted ? ', maior prêmio' : ''}`;
};

export const LastWinnerCard = ({
  name,
  date,
  value,
  state,
  className,
  winnerVideoUrl,
  variant = 'default',
  highlighted = false,
  ...props
}: LastWinnerCardProps) => {
  const openModal = useModalStore((state) => state.openModal);

  const handleClick = () => {
    if (!winnerVideoUrl) return;

    openModal({
      showCloseButton: false,
      className: 'h-auto',
      content: (
        <iframe
          key={winnerVideoUrl}
          src={getYoutubeEmbedUrl(winnerVideoUrl)}
          className={`
            aspect-[9/16]
            w-[min(90vw,calc(70vh*9/16))]
            max-h-[70vh]
            sm:max-h-[85vh]
            md:w-[min(80vw,calc(70vh*9/16))]
            lg:w-[min(60vw,calc(70vh*9/16))]
            md:max-h-[70vh]
            rounded-lg
            max-md:w-[min(90vw,calc(85vh*9/16))]
            max-md:max-h-[85vh]
          `}
          title="YouTube video player"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      ),
    });
  };

  const thumbnailUrl = useMemo(() => {
    if (!winnerVideoUrl) return;

    const videoId = extractYoutubeVideoId(winnerVideoUrl);
    if (!videoId) return;

    return getYoutubeThumbnailUrl({ videoId, quality: EThumbnailQuality.DEFAULT });
  }, [winnerVideoUrl]);

  const styles = variantStyles[variant];
  const ariaLabel = getAriaLabel({ name, variant, highlighted });

  return (
    <div
      className={cn(
        'relative bg-background-variant p-2 rounded-lg flex flex-row h-24 w-fit min-w-44 gap-x-2 max-w-fit',
        styles.container,
        highlighted && 'bg-secondary/10 border-secondary main-prize-glow',
        winnerVideoUrl && 'cursor-pointer',
        className,
      )}
      onClick={handleClick}
      role={ariaLabel ? 'group' : undefined}
      aria-label={ariaLabel}
      {...props}
    >
      <div
        className={cn(
          'flex items-center justify-center',
          thumbnailUrl ? 'aspect-[9/16]' : 'min-w-14',
        )}
      >
        {thumbnailUrl ? (
          <Image
            src={thumbnailUrl}
            alt={`Thumbnail of the winner's video: ${name}`}
            width={62}
            height={62}
            className="object-cover rounded-lg h-full"
          />
        ) : (
          <Image
            src={bolt}
            alt="half bolt background"
            width={62}
            height={62}
            className="object-contain"
          />
        )}
      </div>

      <div className="flex flex-col items-end rounded-lg max-w-32">
        <p
          className={cn(
            TEXT_MAX_WIDTH,
            'text-right font-semibold text-sm text-foreground truncate',
          )}
        >
          {name}
        </p>
        <span
          className={cn(
            TEXT_MAX_WIDTH,
            'text-right font-medium text-xs text-foreground/60 truncate',
          )}
        >
          {formatRelativeTime(new Date(date).getTime())}
        </span>
        <div className="absolute top-0 left-0 -z-10">
          <Image src={halfBolt} alt="half bolt background" width={58} height={58} />
        </div>
        <span
          className={cn(
            TEXT_MAX_WIDTH,
            'text-right font-bold text-base truncate',
            styles.value,
          )}
        >
          {formatCurrency(value)}
        </span>
        <span className="absolute bottom-2 text-right text-xxs text-foreground">
          {getStateAbbreviationByDDD(state)}
        </span>
      </div>
    </div>
  );
};
